import { Log, ILog, LogSource } from './logging.models';
import { LogType } from './log-type.enum';
import { LogLevel } from './log-level.enum';
import { Observable, ReplaySubject } from 'rxjs';
import { environment } from 'src/environments/environment';
import { ILogPersistor, LogPersistor } from './log-persistor.util';
import { LocalStorageUtil } from '../../utils/local-storage.util';
import CBuffer from 'cbuffer'; // https://www.npmjs.com/package/CBuffer#cbuffer-javascript-circular-buffer-utility
declare function require(name:string);
var prune = require('json-prune');
var Hook = require('console-hook');



// Small subset of Cbuffer interface. No types from library.
interface ICBuffer {
  pop: () => any;
  push: (item: any) => number;
  toArray: () => any[];
}

const LOG_STYLES = `border-radius: 3px; padding: 4px 6px;`;
export class Logging {
  /**
   * Will not log if LOG type is lowere than LogLevel
   */
  private static logLevel: LogLevel = LogLevel.Info;
  private static inMemoryLogLimit = environment.maxLogs;
  private static _logs: ICBuffer = new CBuffer(Logging.inMemoryLogLimit);
  private static persistenceInterval = 10000; // 10 seconds.
  private static _logLevelUpdate$: ReplaySubject<LogLevel> = new ReplaySubject(
    1
  );
  private static _logPersistenceCycle: number|undefined;
  private static _isInit = false;
  private static _useConsoleHook = false;

  private static _infoStyle =
    LOG_STYLES +
    `
    color: #002e4c;
    background: #cde7f9;
  `;

  private static _debugStyle =
    LOG_STYLES +
    `
    color: #000000;
    background: #eaeaea;
  `;

  private static _warningStyle =
    LOG_STYLES +
    `
    color: #615704;
    background: #f4f7c2;
  `;

  private static _errorStyle =
    LOG_STYLES +
    `
    color: #920000;
    background: #fff2f2;
  `;

  private static _logPersistor: ILogPersistor;

  private static _claimsBlacklist = ['email', 'family_name', 'nickName', 'sipAddress', 'sipPassword'];
  private static _companyBlacklist = ['city', 'stateOrProvince', 'street', 'zipCode']; // Common header component
  private static _accountBlacklist = ['displayName', 'email', 'lastName', 'loginId', 'mobile', 'name'];
  private static _mivbBlacklist = ['hotdeskExternalNumber', 'appPassword', 'tuiPassCode'];
  /**
   * When JSON.stringifing log data, properties with these names will have thier values replaced with Logging,blacklistReplacementValue
   */
  static propNameBlacklist: string[] = [
    'password',
    'searchText',
    ...Logging._claimsBlacklist,
    ...Logging._companyBlacklist,
    ...Logging._mivbBlacklist,
    ...Logging._accountBlacklist
  ];
  static blacklistReplacementValue = '****';

  /**
   * For a single stored log, will trim the stringified data properties that exceed this many bytes.
   */
  static maxLogDataLength = 4000;


  public static init(logPersistor: ILogPersistor, useConsoleHook: boolean): void {
    this._useConsoleHook = useConsoleHook;
    if (!Logging._isInit && !!logPersistor) {
      if (this._useConsoleHook) {
        Logging.setupConsoleHook(false);
      }
      Logging._logPersistor = logPersistor;
      Logging.beginPersistenceCycle();
      Logging.info(`[LoggingService] init() starting...`);
      // explicitly using local storage and not the WindowUtil helper to avoid a circular reference
      const savedLogLevel = LocalStorageUtil.logLevel;
      const initialLogLevel =
        savedLogLevel !== LogLevel.None ? savedLogLevel : environment.logLevel;
      Logging.setLogLevel(initialLogLevel);
      Logging.saveLogLevel(initialLogLevel);

      window.addEventListener('storage', () => {
        const logLevel = LocalStorageUtil.logLevel;
        if (logLevel !== Logging.logLevel) {
          Logging.setLogLevel(logLevel);
        }
      });
      Logging._isInit = true;
    }
  }

  /*
    Regularly writes the current in-memory logs to persistence,
    i.e. chosen storage API.
  */
  public static beginPersistenceCycle(): void {
    // specifically reference window to avoid ambiguous type of setInterval
    Logging._logPersistenceCycle = window.setInterval(() => {
      const currentLogs: ILog[] = Logging._logs.toArray();
      if (currentLogs.length) {
        try {
          Logging._logPersistor.save(currentLogs);
          Logging._logs = new CBuffer(Logging.inMemoryLogLimit);
        } catch (error) {
          // Save completely failed, we will abandon the persistence cycle to be safe rather than repeatedly trying failed attempts.
          clearInterval(Logging._logPersistenceCycle);
          delete Logging._logPersistenceCycle;
        }
      }
    }, Logging.persistenceInterval);
  }

  public static saveLogLevel(level: LogLevel): void {
    LocalStorageUtil.logLevel = level;
  }

  public static setLogLevel(level: LogLevel): void {
    Logging.info('[LoggingService] setLogLevel()', level);
    Logging.logLevel = level;
    this._logLevelUpdate$.next(Logging.logLevel);
  }

  public static watchLogLevel(): Observable<LogLevel | null> {
    return Logging._logLevelUpdate$;
  }

  public static getLogLevel(): number {
    const logLevel = Logging.logLevel;
    return Number(logLevel);
  }

  private static shouldLog(level: LogLevel): boolean {
    const globalLogLevel = this.getLogLevel();
    return level <= globalLogLevel;
  }

  // to get a snapshot of the logs, not a reference. The list should not be manipulated outside this service
  public static getLogs(): ILog[] {
    try {
      let logs: ILog[];
      if (Logging._logPersistor) {
        const savedLogs = Logging._logPersistor.load();
        const unsavedLogs = Logging._logs.toArray();
        // load persisted logs and combine with in memory.
        logs = [...savedLogs, ...unsavedLogs];
      } else {
        // if no log persistor, can't load logs, just grab in-memory.
        logs = Logging._logs.toArray();
      }
      return logs || [];
    } catch (error) {
      return [new Log(LogType.ERROR, 'Failed to retrieve logs', { error })];
    }
  }

  public static clearLogs(): void {
    Logging._logs = new CBuffer(Logging.inMemoryLogLimit);
    if (Logging._logPersistor) {
      this._logPersistor.clear();
    }
  }

  private static mapLogType2LogLevel(type: LogType): LogLevel {
    let logLevel = LogLevel.None;
    switch (type) {
      case LogType.DEBUG:
        logLevel = LogLevel.Debug;
        break;
      case LogType.INFO:
        logLevel = LogLevel.Info;
        break;
      case LogType.WARNING:
        logLevel = LogLevel.Warn;
      break;
      case LogType.ERROR:
        logLevel = LogLevel.Error;
        break;
    }
    return logLevel;
  }

  /**
   * 
   * @param log 
   * @param forceLog log even if log type is outside logLevel filter  
   */
  public static addLog(log: ILog, forceLog = false): void {
    if (forceLog || this.shouldLog(this.mapLogType2LogLevel(log.logType))) {
      if (log.data) {
        // To prevent data references being mutated after added to logs, stringify now.
        try {
          if (Array.isArray(log.data)) {
            const errorArgs = log.data.filter((arg) => arg instanceof Error);
            const otherArgs = log.data.filter((arg) => !(arg instanceof Error));
            let argsStr = Logging.safeJsonStringify(otherArgs);
            errorArgs.forEach((x) => {
              argsStr += ' ' + JSON.stringify(x, Object.getOwnPropertyNames(x));
            });
            log.data = argsStr;
          } else if (log.data instanceof Error) {
            // https://stackoverflow.com/a/26199752
            log.data = JSON.stringify(
              log.data,
              Object.getOwnPropertyNames(log.data)
            );
          } else {
            log.data = Logging.safeJsonStringify(log.data);
          }
        } catch (error) {
          // log data may container an un-serializable circular reference. Capture the error so we can find these in logs
          let dataStr: string;
          try {
            dataStr = log.data.toString();
          } catch (e) {
            dataStr = error.message;
          }
          log.data = dataStr;
        }

        log.data = this.trimLength(log.data, true);
      }
      this._logs.push(log);
    }
  }

  /**
   * 
   * @param data string to trim; not modified
   * @param max 
   * @param appendMsg if true, will append <trimmed ${len - max}> to trimmed data
   * @returns origional data or trimmed substring if needed
   */
  private static trimLength(data: string, appendMsg: boolean): string {
    const max = Logging.maxLogDataLength;
    if (!data || data.length <= max) return data;
    const len = data.length;
    let trimmed = data.substring(0, max);
    if (appendMsg) {
      trimmed += `<trimmed ${len - max}>`;
    }
    return trimmed;
  }

  /**
   * Wraps JSON.stringify. If it errors then try json-prune.
   * @param data JSON obj
   * @returns stringified JSON
   */
  private static safeJsonStringify(data) {
    let str = '';
    try {
      str = JSON.stringify(data, (k, v) => {
        if (typeof v === 'string' && Logging.propNameBlacklist.includes(k)) {
          return Logging.blacklistReplacementValue;
        }
        return v;
      });  // works well for simple data objects
    } catch (e) {
      // objects of classes may throw an error about un-serializable circular reference
      // see https://stackoverflow.com/questions/13861254/json-stringify-deep-objects
      str = prune(data); // use json-prune to strip the obj down to just simple data properties
    }
    return str;
  }

  public static info(message: string, data?: any, source?: LogSource): void {
    if (this.shouldLog(LogLevel.Info)) {
      const log = new Log(LogType.INFO, message, data, source);
      if (!this._useConsoleHook) {
        this.addLog(log);
      }
      console.log(
        'Info:' + message + (!!data ? ' Data:' : ''),
        !!data ? data : ''
      );
    }
  }

  public static important(
    message: string,
    data?: any,
    source?: LogSource
  ): void {
    const log = new Log(LogType.INFO, message, data, source);
    this.addLog(log, true);
    console.log(
      'Info:' + message + (!!data ? ' Data:' : ''),
      !!data ? data : ''
    );
  }

  public static debug(message: string, data?: any, source?: LogSource): void {
    if (this.shouldLog(LogLevel.Debug)) {
      const log = new Log(LogType.DEBUG, message, data, source);
      if (!this._useConsoleHook) {
        this.addLog(log);
      }
      console.log(
        'Debug:' + message + (!!data ? ' Data:' : ''),
        !!data ? data : ''
      );
    }
  }

  public static warning(message: string, data?: any, source?: LogSource): void {
    if (this.shouldLog(LogLevel.Warn)) {
      const log = new Log(LogType.WARNING, message, data, source);
      if (!this._useConsoleHook) {
        this.addLog(log);
      }
      console.log(
        'Warning:' + message + (!!data ? ' Data:' : ''),
        !!data ? data : ''
      );
    }
  }

  public static error(message: string, data?: any, source?: LogSource): void {
    if (this.shouldLog(LogLevel.Error)) {
      const log = new Log(LogType.ERROR, message, data, source);
      if (!this._useConsoleHook) {
        this.addLog(log);
      }
      console.log(
        'ERROR:' + message + (!!data ? ' - Data:' : ''),
        !!data ? data : ''
      );
    }
  }

  /**
   * 
   * @param silence if true, will not print log on console
   */
  private static setupConsoleHook(silence: boolean) {
    var myHook = Hook(console, silence).attach((method, args) => {
      // method is the console[method] string
      // args is the arguments object passed to console[method]
      let type: LogType;
      switch (method) {
        case "warn":
          type = LogType.WARNING;
          break;
        case "error":
          type = LogType.ERROR;
          break;
        case "debug":
          type = LogType.DEBUG;
          break;
        default:
          type = LogType.INFO;
        
      }
      if (!!args) {
        // args is array-like https://stackoverflow.com/questions/29707568/javascript-difference-between-array-and-array-like-object
        const msg = args[0];
        const arrData: any[] = [args['1'], args['2'], args['3'], args['4'], args['5'], args['6']].filter( i => i !== undefined);  //Array(args);
        const data = arrData.length > 1 ? arrData : arrData[0];
        const log = new Log(type, msg, data, LogSource.APP);
        Logging.addLog(log);
      }
    });
  }

}

export function initLogger(): void {
  window['_mitelLoggingHook'] = Logging;
  const logStorage = sessionStorage;
  Logging.init(new LogPersistor(logStorage), true);
}
